import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { Asset } from '../../../../types/asset/asset';
import { t } from '../../../../types/translation/Translator';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { toFilterString } from '../../../../util/string.util';
import { User } from '../../../../types/user';
import { AssetFilterDropdownContent, AssetFilterInnerContent } from './DropdownContent';
import { AssetTable } from '../../../../ui/pages/Common/AssetTable';
import { AssetTransaction } from '../../../../types/asset_transaction/assetTransaction';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field';
import { ProductMasterDataFilter } from '../ProductMasterDataFilter';
import { CustomFieldFilter } from '../CustomFieldFilter';
import Icon, { IconType } from '../../../../ui/icons/Icon';
import { UserFilter } from '../UserFilter';
import { TagRelation } from '../../../../types/tag';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';

export class AssetFilter extends BaseFilter<Asset> {
  productMasterData: Map<string, ProductMasterData>;
  users: Map<string, User>;
  assets: Map<string, Asset>;
  assetTransactions: Map<string, AssetTransaction>;
  tableSettings: TableSettings<any>;
  customFields: Map<string, CustomField>;
  tagRelations: Map<string, TagRelation>;
  company: Company;

  constructor(
    productMasterData: Map<string, ProductMasterData>,
    users: Map<string, User>,
    assets: Map<string, Asset>,
    assetTransactions: Map<string, AssetTransaction>,
    tableSettings: TableSettings<any>,
    customFields: Map<string, CustomField>,
    tagRelations: Map<string, TagRelation>,
    company: Company,
  ) {
    super(FilterStorageKey.ASSET);
    this.productMasterData = productMasterData;
    this.users = users;
    this.assets = assets;
    this.assetTransactions = assetTransactions;
    this.tableSettings = tableSettings;
    this.customFields = customFields;
    this.tagRelations = tagRelations;
    this.company = company;
  }

  filterOptions(): BaseFilter<any>[] {
    return [
      ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
      new AssetFilter(
        this.productMasterData,
        this.users,
        this.assets,
        this.assetTransactions,
        this.tableSettings,
        this.customFields,
        this.tagRelations,
        this.company,
      ),
      new UserFilter(this.users),
      ...CustomFieldFilter.for(CustomFieldEntityType.productMasterData, this.productMasterData, this.customFields),
      ...CustomFieldFilter.for(
        CustomFieldEntityType.asset,
        new Map([...this.assets.values()].map(asset => [asset.id, asset.product])),
        this.customFields,
      ),
    ];
  }

  toLabel = () => {
    return t(TranslationKey.asset).plural.label;
  };

  toCount(props: BaseFilterProps) {
    return props.filterProps.product?.size || 0;
  }

  toDropdownContent(props: BaseFilterProps) {
    return (
      <AssetFilterDropdownContent filter={this} filterProps={props.filterProps} setFilterProps={props.setFilterProps} />
    );
  }

  toInnerContent = (props: BaseFilterProps) => {
    return (
      <AssetFilterInnerContent filter={this} filterProps={props.filterProps} setFilterProps={props.setFilterProps} />
    );
  };

  toIcon = () => {
    return <Icon type={IconType.wallet1} size={'size-5'} className={this.className} />;
  };

  search = (item: Asset, query: string) => {
    return AssetTable.filter(
      item,
      toFilterString(query),
      this.productMasterData,
      this.users,
      this.assets,
      this.assetTransactions,
      this.tableSettings,
    );
  };

  filteredItems(items: Asset[], query: string, filterProps: FilterProps) {
    return items.filter(item => {
      const productMasterData = this.productMasterData.get(item.product.productMasterDataId);
      const user = this.users.get(item.location?.entityId || '');

      return [
        UserFilter.filter(filterProps, user),
        ProductMasterDataFilter.filter(filterProps, productMasterData, this.customFields, this.tagRelations),
        AssetFilter.filter(filterProps, item, this.customFields),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(filterProps: FilterProps, item?: Asset, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    return CustomFieldFilter.filter(filterProps, item?.product, customFields, CustomFieldEntityType.asset);
  }
}
